import React, { useEffect, useMemo, useRef, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useQuery, gql } from '@apollo/client'
import orderBy from 'lodash/orderBy'
import {
  Box,
  Button,
  Container,
  Grid,
  Link,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import PeopleAltOutlined from '@mui/icons-material/PeopleAltOutlined'
import PersonAddAlt1Outlined from '@mui/icons-material/PersonAddAlt1Outlined'
import StarOutlineRounded from '@mui/icons-material/StarOutlineRounded'
import RemoveShoppingCartOutlined from '@mui/icons-material/RemoveShoppingCartOutlined'
import FavoriteBorderRounded from '@mui/icons-material/FavoriteBorderRounded'
import HourglassEmptyRounded from '@mui/icons-material/HourglassEmptyRounded'
import HourglassBottomRounded from '@mui/icons-material/HourglassBottomRounded'
import BedtimeOutlined from '@mui/icons-material/BedtimeOutlined'
import PhoneDisabledOutlined from '@mui/icons-material/PhoneDisabledOutlined'
import RepeatRounded from '@mui/icons-material/RepeatRounded'
import EmojiEventsOutlined from '@mui/icons-material/EmojiEventsOutlined'
import PlaceOutlined from '@mui/icons-material/PlaceOutlined'
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined'
import ShoppingBagOutlined from '@mui/icons-material/ShoppingBagOutlined'
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined'
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded'
import Header from '../components/Headers/Header'
import CustomLoader from '../components/Loader/CustomLoader'
import DataTable from '../components/DataTable'
import TableHeader from '../components/TableHeader'
import SearchBar from '../components/TableHeader/SearchBar'
import { StatTile, SectionCard, raisedButtonSx } from '../components/UI/Depth'
import { customStyles } from '../utils/tableCustomStyles'
import { depth } from '../utils/depth'
import { getCustomerMarketing } from '../apollo'

const GET_CUSTOMER_MARKETING = gql`
  ${getCustomerMarketing}
`

// 0 = all time; CUSTOM picks a from/to date range
const PERIODS = [7, 30, 60, 90, 0]
const CUSTOM = 'custom'
const dateFieldSx = {
  width: 160,
  bgcolor: 'common.white',
  '& .MuiOutlinedInput-root': { borderRadius: '12px' }
}

// YYYY-MM-DD in the admin's local time, for <input type="date">
const toDateInput = date => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 10)
}
const formatDay = value =>
  new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })

// Each tile doubles as a table filter. `summary` is the count's key on the
// server summary, `match` picks the same customers from the rows.
const TILES = [
  {
    id: 'all',
    label: 'TotalCustomers',
    icon: PeopleAltOutlined,
    tone: 'orange',
    summary: 'totalCustomers',
    match: () => true
  },
  {
    id: 'new',
    label: 'NewCustomers',
    icon: PersonAddAlt1Outlined,
    tone: 'blue',
    summary: 'newCustomers',
    match: c => c.isNew,
    windowed: true
  },
  {
    id: 'firstTime',
    label: 'FirstTimeOrderers',
    icon: StarOutlineRounded,
    tone: 'violet',
    summary: 'firstTimeOrderers',
    match: c => c.isFirstTimeInWindow,
    windowed: true
  },
  {
    id: 'never',
    label: 'NeverOrdered',
    icon: RemoveShoppingCartOutlined,
    tone: 'slate',
    summary: 'neverOrdered',
    match: c => c.segment === 'never'
  },
  {
    id: 'active',
    label: 'ActiveCustomers',
    icon: FavoriteBorderRounded,
    tone: 'green',
    summary: 'activeCustomers',
    // Follows the period picker: ordered at least once in the window.
    match: c => c.ordersInWindow > 0,
    windowed: true,
    hint: 'ActiveHint'
  },
  {
    id: 'inactive30',
    label: 'Inactive30',
    icon: HourglassEmptyRounded,
    tone: 'amber',
    summary: 'inactive30',
    match: c => c.segment === 'inactive30',
    hint: 'Inactive30Hint'
  },
  {
    id: 'inactive60',
    label: 'Inactive60',
    icon: HourglassBottomRounded,
    tone: 'amber',
    summary: 'inactive60',
    match: c => c.segment === 'inactive60',
    hint: 'Inactive60Hint'
  },
  {
    id: 'dormant90',
    label: 'Dormant90',
    icon: BedtimeOutlined,
    tone: 'rose',
    summary: 'dormant90',
    match: c => c.segment === 'dormant90',
    hint: 'Dormant90Hint'
  },
  {
    id: 'noPhone',
    label: 'IncompleteRegistration',
    icon: PhoneDisabledOutlined,
    tone: 'rose',
    summary: 'incompleteRegistration',
    match: c => !c.hasPhone,
    hint: 'NoPhoneHint'
  },
  {
    id: 'repeat',
    label: 'RepeatCustomers',
    icon: RepeatRounded,
    tone: 'blue',
    summary: 'repeatCustomers',
    match: c => c.totalOrders >= 2,
    hint: 'RepeatHint'
  },
  {
    // Shows the #1 customer; the list below ranks everyone by spend.
    id: 'topSpenders',
    label: 'TopSpender',
    icon: EmojiEventsOutlined,
    tone: 'orange',
    match: c => c.periodSpent > 0,
    sortBySpend: true,
    windowed: true
  }
]

const SEGMENT_STYLE = {
  ordered30: {
    label: 'SegmentOrdered30',
    color: '#15803D',
    bg: 'rgba(21,128,61,0.1)'
  },
  inactive30: {
    label: 'Inactive30',
    color: '#B45309',
    bg: 'rgba(217,119,6,0.12)'
  },
  inactive60: {
    label: 'Inactive60',
    color: '#C2410C',
    bg: 'rgba(234,88,12,0.12)'
  },
  dormant90: {
    label: 'Dormant90',
    color: '#BE123C',
    bg: 'rgba(190,18,60,0.1)'
  },
  never: { label: 'NeverOrdered', color: '#475569', bg: 'rgba(71,85,105,0.1)' }
}

const pounds = value => `£${Number(value || 0).toFixed(2)}`
const shortDate = value =>
  value ? new Date(value).toLocaleDateString('en-GB') : '-'
const mapLink = row =>
  row.latitude !== null && row.longitude !== null
    ? `https://www.google.com/maps?q=${row.latitude},${row.longitude}`
    : null

// Quote every cell, and defuse anything a spreadsheet would run as a formula
// (addresses and names are typed by customers). Phone numbers like +44... stay.
// Excel turns long numbers like +447… into 4.47E+11. Emitting a digits-only
// value as ="…" makes Excel keep it as text. Only digits/spaces/+ get here,
// so nothing customer-typed can be run as a formula.
const asText = value =>
  value && /^\+?[\d\s]+$/.test(value) ? { raw: `"=""${value}"""` } : value

const csvCell = value => {
  if (value && value.raw) return value.raw
  let text = value === null || value === undefined ? '' : String(value)
  if (/^[=@\t\r]/.test(text) || /^[+-][^\d\s]/.test(text)) text = `'${text}`
  return `"${text.replace(/"/g, '""')}"`
}

const CSV_COLUMNS = [
  ['Name', c => c.name],
  ['Email', c => c.email],
  ['Email verified', c => (c.emailIsVerified ? 'Yes' : 'No')],
  ['Phone', c => asText(c.phone)],
  ['Phone verified', c => (c.phoneIsVerified ? 'Yes' : 'No')],
  ['Segment', c => c.segment],
  ['Joined', c => shortDate(c.joinedAt)],
  ['Total orders', c => c.totalOrders],
  ['Orders in period', c => c.ordersInWindow],
  ['Spent in period (GBP)', c => Number(c.periodSpent || 0).toFixed(2)],
  ['Total spent (GBP)', c => Number(c.totalSpent || 0).toFixed(2)],
  ['Average order (GBP)', c => Number(c.averageOrderValue || 0).toFixed(2)],
  ['Coupon orders', c => c.couponOrders],
  ['First order', c => shortDate(c.firstOrderAt)],
  ['Last order', c => shortDate(c.lastOrderAt)],
  ['Address', c => c.address],
  ['Address details', c => c.addressDetails],
  ['Address source', c => c.addressSource],
  ['Postcode area', c => c.postcodeArea],
  ['Zone', c => c.zone],
  ['Latitude', c => c.latitude],
  ['Longitude', c => c.longitude],
  ['Favourite store', c => c.favouriteStore],
  ['Referred', c => (c.referred ? 'Yes' : 'No')],
  ['Credits', c => Number(c.credits || 0).toFixed(2)],
  ['Offer notifications', c => (c.offerNotifications ? 'On' : 'Off')]
]

const downloadCsv = (rows, name) => {
  const lines = [
    CSV_COLUMNS.map(([header]) => csvCell(header)).join(','),
    ...rows.map(row =>
      CSV_COLUMNS.map(([, get]) => csvCell(get(row))).join(',')
    )
  ]
  // BOM so Excel reads £ and accented names as UTF-8
  const blob = new Blob(['﻿' + lines.join('\r\n')], {
    type: 'text/csv;charset=utf-8'
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

// Customers per area, busiest first. Keyed by postcode district, falling back
// to the zone when the address has no recognisable postcode.
const topAreas = (customers, limit = 10) => {
  const counts = new Map()
  customers.forEach(c => {
    const area = c.postcodeArea || c.zone
    if (!area) return
    const entry = counts.get(area) || { area, customers: 0, spent: 0 }
    entry.customers += 1
    entry.spent += c.periodSpent || 0
    counts.set(area, entry)
  })
  return orderBy(
    [...counts.values()],
    ['customers', 'spent'],
    ['desc', 'desc']
  ).slice(0, limit)
}

const CustomerMarketing = ({ t }) => {
  const [period, setPeriod] = useState(30)
  const [fromDate, setFromDate] = useState(() =>
    toDateInput(new Date(Date.now() - 30 * 86400000))
  )
  const [toDate, setToDate] = useState(() => toDateInput(new Date()))
  const isCustom = period === CUSTOM
  // Custom ranges have no rolling day count; badges fall back to segments.
  const days = isCustom ? 0 : period
  const [tileId, setTileId] = useState('all')
  const [area, setArea] = useState('')
  const [store, setStore] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const listRef = useRef(null)

  // A tile is a shortcut to the people behind its number: filter the list
  // and bring it into view.
  const showList = id => {
    setTileId(id)
    setSearchQuery('')
    if (listRef.current) {
      listRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const { data, loading, error } = useQuery(GET_CUSTOMER_MARKETING, {
    // Send the admin's local day bounds so "1 Oct" means their 1 Oct.
    variables: isCustom
      ? {
          startDate: new Date(`${fromDate}T00:00:00`).toISOString(),
          endDate: new Date(`${toDate}T23:59:59.999`).toISOString()
        }
      : { days },
    fetchPolicy: 'cache-and-network'
  })
  const summary = data && data.customerMarketing.summary
  const topStores = (data && data.customerMarketing.topStores) || []
  const topProducts = (data && data.customerMarketing.topProducts) || []
  // Orders/spend shown on this page follow the picked period, not lifetime.
  const customers = useMemo(
    () =>
      ((data && data.customerMarketing.customers) || []).map(c => ({
        ...c,
        periodSpent: c.spentInWindow || 0,
        daysSinceOrder: c.lastOrderAt
          ? Math.floor((Date.now() - new Date(c.lastOrderAt)) / 86400000)
          : null,
        periodAvg: c.ordersInWindow
          ? (c.spentInWindow || 0) / c.ordersInWindow
          : 0
      })),
    [data]
  )
  const periodLabel = isCustom
    ? fromDate === toDate
      ? formatDay(fromDate)
      : `${formatDay(fromDate)} – ${formatDay(toDate)}`
    : days
    ? `${days}d`
    : t('AllTime')

  const tile = TILES.find(item => item.id === tileId) || TILES[0]

  const filtered = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    const rows = customers.filter(c => {
      if (!tile.match(c)) return false
      if (area && (c.postcodeArea || c.zone) !== area) return false
      if (store && c.favouriteStore !== store) return false
      if (query.length < 2) return true
      return [
        c.name,
        c.email,
        c.phone,
        c.address,
        c.postcodeArea,
        c.zone,
        c.favouriteStore
      ].some(value => (value || '').toLowerCase().includes(query))
    })
    return tile.sortBySpend ? orderBy(rows, ['periodSpent'], ['desc']) : rows
  }, [customers, tile, area, store, searchQuery])

  const stores = useMemo(
    () =>
      [
        ...new Set(customers.map(c => c.favouriteStore).filter(Boolean))
      ].sort((a, b) => a.localeCompare(b)),
    [customers]
  )

  const topCustomers = useMemo(
    () =>
      orderBy(
        customers.filter(c => c.periodSpent > 0),
        ['periodSpent'],
        ['desc']
      ).slice(0, 10),
    [customers]
  )
  const areas = useMemo(() => topAreas(customers), [customers])

  const exportName = [
    'customers',
    tile.id !== 'all' ? tile.id : null,
    isCustom ? `${fromDate}_to_${toDate}` : null,
    area || null,
    store || null
  ]
    .filter(Boolean)
    .join('-')
    .toLowerCase()
    .replace(/\s+/g, '')

  const tileLabel = item =>
    item.windowed ? `${t(item.label)} (${periodLabel})` : t(item.label)

  const columns = [
    {
      name: t('Customer'),
      selector: 'name',
      sortable: true,
      minWidth: '200px',
      cell: row => (
        <Box sx={{ py: 1, minWidth: 0 }}>
          <Box sx={{ fontWeight: 600 }}>{row.name || '-'}</Box>
          <Box sx={{ fontSize: 12, color: 'text.disabled' }}>{row.email}</Box>
          {row.couponOrders > 0 || row.referred ? (
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
              {row.couponOrders > 0 ? (
                <Pill color="#B45309" bg="rgba(217,119,6,0.12)">
                  {t('CouponUser')}
                </Pill>
              ) : null}
              {row.referred ? (
                <Pill color="#6D28D9" bg="rgba(124,58,237,0.1)">
                  {t('Referred')}
                </Pill>
              ) : null}
            </Box>
          ) : null}
        </Box>
      )
    },
    {
      name: t('Phone'),
      selector: 'phone',
      sortable: true,
      minWidth: '140px',
      cell: row =>
        row.hasPhone ? (
          row.phone
        ) : (
          <Pill color="#BE123C" bg="rgba(190,18,60,0.1)">
            {t('Missing')}
          </Pill>
        )
    },
    {
      name: t('Status'),
      selector: 'segment',
      sortable: true,
      minWidth: '150px',
      cell: row => {
        const style = SEGMENT_STYLE[row.segment] || SEGMENT_STYLE.never
        // Narrow the "ordered" badge to the picked period when it's shorter.
        const withinPeriod =
          row.segment === 'ordered30' &&
          days > 0 &&
          days < 30 &&
          Date.now() - new Date(row.lastOrderAt).getTime() <= days * 86400000
        return (
          <Pill color={style.color} bg={style.bg}>
            {withinPeriod
              ? t('OrderedWithinDays', { count: days })
              : t(style.label)}
          </Pill>
        )
      }
    },
    {
      name: `${t('Orders')} (${periodLabel})`,
      selector: 'ordersInWindow',
      sortable: true,
      width: '90px'
    },
    {
      name: `${t('Spent')} (${periodLabel})`,
      selector: 'periodSpent',
      sortable: true,
      width: '110px',
      cell: row => pounds(row.periodSpent)
    },
    {
      name: t('AvgOrder'),
      selector: 'periodAvg',
      sortable: true,
      width: '110px',
      cell: row => pounds(row.periodAvg)
    },
    {
      name: t('LastOrder'),
      selector: 'lastOrderAt',
      sortable: true,
      width: '120px',
      cell: row => shortDate(row.lastOrderAt)
    },
    {
      name: t('DaysSinceOrder'),
      selector: 'daysSinceOrder',
      sortable: true,
      width: '110px',
      cell: row => (row.daysSinceOrder === null ? '-' : row.daysSinceOrder)
    },
    {
      name: t('Joined'),
      selector: 'joinedAt',
      sortable: true,
      width: '120px',
      cell: row => shortDate(row.joinedAt)
    },
    {
      name: t('Location'),
      selector: 'address',
      sortable: true,
      minWidth: '260px',
      cell: row => {
        const link = mapLink(row)
        if (!row.address) return '-'
        return (
          <Box sx={{ py: 1, minWidth: 0 }}>
            <Box sx={{ fontSize: 13 }}>{row.address}</Box>
            <Box sx={{ fontSize: 12, color: 'text.disabled' }}>
              {[
                row.postcodeArea,
                row.zone,
                row.addressSource === 'saved' ? t('SavedAddress') : null
              ]
                .filter(Boolean)
                .join(' · ')}
              {link ? (
                <>
                  {' · '}
                  <Link href={link} target="_blank" rel="noopener noreferrer">
                    {t('Map')}
                  </Link>
                </>
              ) : null}
            </Box>
          </Box>
        )
      }
    },
    {
      name: t('FavouriteStore'),
      selector: 'favouriteStore',
      sortable: true,
      minWidth: '150px',
      cell: row => row.favouriteStore || '-'
    },
    {
      name: t('Coupons'),
      selector: 'couponOrders',
      sortable: true,
      width: '100px'
    },
    {
      name: t('Credits'),
      selector: 'credits',
      sortable: true,
      width: '100px',
      cell: row => pounds(row.credits)
    },
    {
      name: t('Offers'),
      selector: 'offerNotifications',
      sortable: true,
      width: '90px',
      cell: row => (row.offerNotifications ? t('On') : t('Off'))
    }
  ]

  // Blank values (never ordered, no address…) stay at the bottom both ways,
  // otherwise a descending sort opens on hundreds of empty rows.
  const isBlank = value => value === null || value === undefined || value === ''
  const sortRows = (rows, field, direction) =>
    orderBy(
      rows,
      [
        row => (isBlank(row[field]) ? 1 : 0),
        row =>
          typeof row[field] === 'string' ? row[field].toLowerCase() : row[field]
      ],
      ['asc', direction]
    )

  // Back to page 1 whenever the filtered set changes (RDT toggles on change)
  const [resetPage, setResetPage] = useState(false)
  useEffect(() => {
    setResetPage(value => !value)
  }, [tileId, area, store, searchQuery, period, fromDate, toDate])

  const pickArea = value => {
    setArea(value)
    if (value && listRef.current) {
      listRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <>
      <Header
        action={
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Select
              size="small"
              value={period}
              onChange={e => setPeriod(e.target.value)}
              sx={{
                minWidth: 150,
                bgcolor: 'common.white',
                borderRadius: '12px'
              }}>
              {PERIODS.map(value => (
                <MenuItem key={value} value={value}>
                  {value ? t('LastNDays', { count: value }) : t('AllTime')}
                </MenuItem>
              ))}
              <MenuItem value={CUSTOM}>{t('CustomRange')}</MenuItem>
            </Select>
            {isCustom ? (
              <>
                <TextField
                  type="date"
                  size="small"
                  label={t('From')}
                  value={fromDate}
                  onChange={e => {
                    const value = e.target.value
                    if (!value) return
                    setFromDate(value)
                    // A typed start after the end would make the server
                    // silently fall back to 30 days; drag the end along.
                    if (value > toDate) setToDate(value)
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: toDate }}
                  sx={dateFieldSx}
                />
                <TextField
                  type="date"
                  size="small"
                  label={t('To')}
                  value={toDate}
                  onChange={e => e.target.value && setToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: fromDate, max: toDateInput(new Date()) }}
                  sx={dateFieldSx}
                />
              </>
            ) : null}
            <Button
              startIcon={<FileDownloadOutlined />}
              disabled={!filtered.length}
              onClick={() => downloadCsv(filtered, exportName)}
              sx={raisedButtonSx}>
              {`${t('ExportCSV')} (${filtered.length})`}
            </Button>
          </Box>
        }
      />
      <Container maxWidth={false} disableGutters>
        {error ? (
          <Typography color="error" sx={{ mb: 2 }}>
            {error.message}
          </Typography>
        ) : null}
        {loading && !data ? (
          <CustomLoader />
        ) : (
          <>
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              {TILES.map(item => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => showList(item.id)}
                    onKeyDown={e => e.key === 'Enter' && showList(item.id)}
                    sx={{
                      position: 'relative',
                      height: '100%',
                      cursor: 'pointer',
                      borderRadius: `${depth.radius}px`,
                      outline: tileId === item.id ? '2px solid #F4661F' : 'none'
                    }}>
                    <StatTile
                      icon={item.icon}
                      tone={item.tone}
                      label={tileLabel(item)}
                      value={
                        item.sortBySpend
                          ? topCustomers.length
                            ? pounds(topCustomers[0].periodSpent)
                            : '-'
                          : summary
                          ? summary[item.summary]
                          : '-'
                      }
                      hint={
                        item.sortBySpend
                          ? topCustomers.length
                            ? topCustomers[0].name
                            : null
                          : item.hint
                          ? t(item.hint)
                          : null
                      }
                    />
                    <Tooltip
                      title={
                        tileId === item.id
                          ? t('ShowingBelow')
                          : t('ViewCustomers')
                      }>
                      <ArrowDownwardRounded
                        sx={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          fontSize: 18,
                          color: tileId === item.id ? '#F4661F' : 'grey.400'
                        }}
                      />
                    </Tooltip>
                  </Box>
                </Grid>
              ))}
              {/* Fixed rolling windows, not tied to the period picker */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <StatTile
                  icon={ShoppingBagOutlined}
                  tone="green"
                  label={t('TotalOrders')}
                  value={
                    <Box component="span" sx={{ display: 'flex', gap: 2.5 }}>
                      {[7, 14, 30].map(n => (
                        <Box component="span" key={n}>
                          {summary ? summary[`orders${n}`] : '-'}
                          <Box
                            component="span"
                            sx={{
                              display: 'block',
                              fontSize: 12,
                              fontWeight: 600,
                              color: 'text.disabled',
                              letterSpacing: 0
                            }}>
                            {`${n}d`}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  }
                />
              </Grid>
            </Grid>

            <Box
              ref={listRef}
              sx={{
                width: '100%',
                overflowX: 'auto',
                mb: 3,
                scrollMarginTop: 16
              }}>
              <DataTable
                subHeader
                subHeaderComponent={
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'center',
                      flexWrap: 'wrap'
                    }}>
                    <Select
                      size="small"
                      displayEmpty
                      value={store}
                      onChange={e => setStore(e.target.value)}
                      sx={{
                        minWidth: 200,
                        bgcolor: 'common.white',
                        borderRadius: '12px'
                      }}>
                      <MenuItem value="">{t('AllStores')}</MenuItem>
                      {stores.map(name => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                    <SearchBar
                      value={searchQuery}
                      placeholder={t('SearchCustomers')}
                      onChange={e => setSearchQuery(e.target.value)}
                    />
                  </Box>
                }
                title={
                  <TableHeader
                    title={[tileLabel(tile), area, store]
                      .filter(Boolean)
                      .join(' · ')}
                    count={filtered.length}
                  />
                }
                columns={columns}
                data={filtered}
                pagination
                paginationPerPage={25}
                paginationResetDefaultPage={resetPage}
                sortFunction={sortRows}
                customStyles={customStyles}
                highlightOnHover
              />
            </Box>

            <Grid container spacing={2.5}>
              <Grid item xs={12} lg={7}>
                <SectionCard
                  icon={EmojiEventsOutlined}
                  tone="amber"
                  title={t('TopCustomersBySpend')}
                  subtitle={t('TopCustomersSubtitle')}>
                  {topCustomers.length ? (
                    topCustomers.map((c, index) => (
                      <RankRow
                        key={c._id}
                        rank={index + 1}
                        title={c.name || c.email || '-'}
                        subtitle={[
                          c.phone || t('NoPhone'),
                          c.postcodeArea || c.zone,
                          c.favouriteStore
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                        value={pounds(c.periodSpent)}
                        meta={`${c.ordersInWindow} ${t(
                          'Orders'
                        ).toLowerCase()}`}
                      />
                    ))
                  ) : (
                    <Empty>{t('NoOrdersYet')}</Empty>
                  )}
                </SectionCard>
              </Grid>
              <Grid item xs={12} lg={5}>
                <SectionCard
                  icon={PlaceOutlined}
                  tone="blue"
                  title={t('CustomerLocations')}
                  subtitle={t('CustomerLocationsSubtitle')}
                  action={
                    area ? (
                      <Button size="small" onClick={() => setArea('')}>
                        {t('Clear')}
                      </Button>
                    ) : null
                  }>
                  {areas.length ? (
                    areas.map((entry, index) => (
                      <Box
                        key={entry.area}
                        onClick={() =>
                          pickArea(area === entry.area ? '' : entry.area)
                        }
                        sx={{
                          cursor: 'pointer',
                          borderRadius: '10px',
                          bgcolor:
                            area === entry.area
                              ? 'rgba(244,102,31,0.08)'
                              : 'transparent'
                        }}>
                        <RankRow
                          rank={index + 1}
                          title={entry.area}
                          subtitle={`${pounds(entry.spent)} ${t(
                            'Spent'
                          ).toLowerCase()}`}
                          value={entry.customers}
                          meta={t('Customers').toLowerCase()}
                        />
                      </Box>
                    ))
                  ) : (
                    <Empty>{t('NoLocationsYet')}</Empty>
                  )}
                </SectionCard>
              </Grid>
              <Grid item xs={12} lg={6}>
                <SectionCard
                  icon={StorefrontOutlined}
                  tone="orange"
                  title={`${t('TopStores')} (${periodLabel})`}
                  subtitle={t('TopSellersSubtitle')}>
                  {topStores.length ? (
                    topStores.map((s, index) => (
                      <RankRow
                        key={s._id}
                        rank={index + 1}
                        title={s.name || '-'}
                        subtitle={`${s.orders} ${t('Orders').toLowerCase()}`}
                        value={pounds(s.sales)}
                      />
                    ))
                  ) : (
                    <Empty>{t('NoSalesInPeriod')}</Empty>
                  )}
                </SectionCard>
              </Grid>
              <Grid item xs={12} lg={6}>
                <SectionCard
                  icon={ShoppingBagOutlined}
                  tone="violet"
                  title={`${t('TopProducts')} (${periodLabel})`}
                  subtitle={t('TopSellersSubtitle')}>
                  {topProducts.length ? (
                    topProducts.map((p, index) => (
                      <RankRow
                        key={p._id}
                        rank={index + 1}
                        title={p.title || '-'}
                        subtitle={`${p.store} · ${p.quantity} ${t(
                          'SoldCount'
                        )}`}
                        value={pounds(p.sales)}
                      />
                    ))
                  ) : (
                    <Empty>{t('NoSalesInPeriod')}</Empty>
                  )}
                </SectionCard>
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </>
  )
}

const Pill = ({ color, bg, children }) => (
  <Box
    component="span"
    sx={{
      px: 1.25,
      py: 0.25,
      borderRadius: '999px',
      fontSize: 12,
      fontWeight: 600,
      color,
      bgcolor: bg,
      whiteSpace: 'nowrap'
    }}>
    {children}
  </Box>
)

const RankRow = ({ rank, title, subtitle, value, meta }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      px: 1,
      py: 1.25,
      borderBottom: '1px solid rgba(16,24,40,0.06)'
    }}>
    <Box
      sx={{ width: 24, fontWeight: 700, color: 'text.disabled', fontSize: 13 }}>
      {rank}
    </Box>
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography noWrap sx={{ fontSize: 12, color: 'text.disabled' }}>
        {subtitle}
      </Typography>
    </Box>
    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{value}</Typography>
      <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
        {meta}
      </Typography>
    </Box>
  </Box>
)

const Empty = ({ children }) => (
  <Typography
    sx={{ py: 3, textAlign: 'center', color: 'text.disabled', fontSize: 14 }}>
    {children}
  </Typography>
)

export default withTranslation()(CustomerMarketing)
